import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { parseProductsFile } from "./parseProductsFile";
import {
  streamBulkStage,
  BulkStageRequestError,
  BulkStageStreamTimeoutError,
} from "./bulkStageClient";
import {
  saveLastResult,
  loadLastResult,
  clearLastResult,
} from "./bulkStagePersistence";

// State machine for the file → parse → client-validate → stream → result flow.
// One reducer keeps the transitions explicit and isolates the stream reader
// from React state — the reader just calls dispatch({type:"STREAM_EVENT",event})
// and the reducer fans out by event.event ("started" | "row" | "summary").
//
// The dispatcher backend emits a `destination` of "live" | "staged" | "rejected"
// per row. We track those three counts directly. Legacy `status` is read only
// as a fallback when `destination` is absent (defensive against older backends).

export const PHASES = Object.freeze({
  IDLE: "idle",
  PARSING: "parsing",
  CLIENT_ERROR: "client-error",
  UPLOADING: "uploading",
  STREAMING: "streaming",
  DONE_SUCCESS: "done-success",
  DONE_WITH_ERRORS: "done-with-errors",
  // Stream ended (cleanly or via timeout) without a final `summary` event,
  // but at least one `row` event arrived first. The server processed some
  // rows and the user needs to know which ones — distinct from
  // TRANSPORT_ERROR (we never got past the request).
  INCOMPLETE: "incomplete",
  CANCELLED: "cancelled",
  TRANSPORT_ERROR: "transport-error",
});

// Mirrors the dispatcher's Tier 1 required-field set (BULK_STAGE_REQUIRED_FIELDS
// in app/services/admin/temporaryproduct.py). Each missing field becomes one
// synthetic ERR_REQUIRED_FIELD_MISSING entry on that row's failure record so the
// rendering path is identical to server-emitted failures.
//
// Barcode ONLY. Staging is a holding pen, not the catalogue — every other column
// is filled in from the pending-products grid after upload. Blocking the file
// here just sends the user back to Excel, which is the worst place to fix it.
// Barcode stays because it's the row identity: the internal SKU, the unique key
// on temporary_product, the dedup target, and the join key the bulk image import
// matches photos on.
//
// KEEP IN SYNC WITH THE BACKEND. This list must never be stricter than
// BULK_STAGE_REQUIRED_FIELDS — a field required here but not there is rejected
// client-side without the server ever being asked, which is invisible in the
// backend logs and looks like the importer is broken.
const REQUIRED_FIELDS = ["barcode"];
const MAX_ROWS = 500;

// Maps a row event to one of the three buckets. Prefer the new `destination`
// field; fall back to the legacy `status` if a deployment is mid-rollout and
// the server hasn't started emitting `destination` yet.
function bucketForRowEvent(ev) {
  if (ev.destination === "live" || ev.destination === "staged" || ev.destination === "rejected") {
    return ev.destination;
  }
  return ev.status === "ok" ? "staged" : "rejected";
}

const initialState = {
  phase: PHASES.IDLE,
  file: null,
  parsedRows: null,
  total: 0,
  processed: 0,
  live: 0,
  staged: 0,
  rejected: 0,
  rowFailures: [], // [{rowIndex (0-based), name, barcode, errors[]}]
  summary: null,   // {total, live, staged, rejected, by_code}
  requestError: null,   // {code, message, params} for HTTP 400
  transportError: null, // string for fetch/network failures
  incompleteReason: null, // why we ended in INCOMPLETE phase (for copy)
  // ms timestamp when the server's first `started` event arrived. Used by
  // the progress UI to derive elapsed time, rows/sec, and ETA. null until
  // the stream begins; not persisted (only meaningful while streaming).
  streamStartedAt: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "RESET":
    case "FILE_CLEARED":
      return initialState;

    case "PARSING_STARTED":
      return {
        ...initialState,
        phase: PHASES.PARSING,
        file: action.file,
      };

    case "PARSE_FAILED":
      return {
        ...initialState,
        phase: PHASES.TRANSPORT_ERROR,
        file: state.file,
        transportError: action.message,
      };

    case "CLIENT_VALIDATION_FAILED":
      return {
        ...state,
        phase: PHASES.CLIENT_ERROR,
        parsedRows: action.parsedRows,
        rowFailures: action.failures,
      };

    case "UPLOAD_STARTED":
      return {
        ...initialState,
        phase: PHASES.UPLOADING,
        file: state.file,
        parsedRows: action.parsedRows,
        total: action.parsedRows.length,
      };

    case "STREAM_EVENT": {
      const ev = action.event;
      if (!ev || typeof ev !== "object") return state;

      if (ev.event === "started") {
        return {
          ...state,
          phase: PHASES.STREAMING,
          // Trust the server's `total` over our own row count — it's authoritative.
          total: typeof ev.total === "number" ? ev.total : state.total,
          streamStartedAt: Date.now(),
        };
      }

      if (ev.event === "row") {
        const bucket = bucketForRowEvent(ev);
        const next = {
          ...state,
          processed: state.processed + 1,
          live: state.live + (bucket === "live" ? 1 : 0),
          staged: state.staged + (bucket === "staged" ? 1 : 0),
          rejected: state.rejected + (bucket === "rejected" ? 1 : 0),
        };
        if (bucket === "rejected") {
          next.rowFailures = [
            ...state.rowFailures,
            {
              rowIndex: ev.row,        // 0-based, render as +1 in UI
              name: ev.name || null,
              barcode: ev.barcode || null,
              errors: Array.isArray(ev.errors) ? ev.errors : [],
            },
          ];
        }
        return next;
      }

      if (ev.event === "summary") {
        const live = ev.live ?? state.live;
        const staged = ev.staged ?? state.staged;
        // `failed` is a back-compat alias for `rejected` from the server side.
        const rejected = ev.rejected ?? ev.failed ?? state.rejected;
        return {
          ...state,
          phase: rejected > 0 ? PHASES.DONE_WITH_ERRORS : PHASES.DONE_SUCCESS,
          summary: {
            total: ev.total ?? state.total,
            live,
            staged,
            rejected,
            by_code: ev.by_code || {},
          },
        };
      }

      // Unknown event type — log and ignore so a future backend addition
      // doesn't break the stream.
      console.warn("[useBulkStageUpload] unknown stream event", ev);
      return state;
    }

    case "REQUEST_ERROR":
      return {
        ...state,
        phase: PHASES.TRANSPORT_ERROR,
        requestError: action.error,
      };

    case "TRANSPORT_ERROR":
      return {
        ...state,
        phase: PHASES.TRANSPORT_ERROR,
        transportError: action.message,
      };

    case "STREAM_ABORTED":
      return {
        ...state,
        phase: PHASES.CANCELLED,
      };

    case "STREAM_INCOMPLETE":
      return {
        ...state,
        phase: PHASES.INCOMPLETE,
        // Stash the reason (timeout vs clean-close-no-summary) so the card
        // can vary copy. Doesn't affect persistence; both shapes resume the
        // same way.
        incompleteReason: action.reason || "stream-ended-without-summary",
      };

    case "HYDRATE_FROM_PERSIST": {
      const lr = action.lastResult || {};
      return {
        ...initialState,
        phase: lr.phase || PHASES.IDLE,
        // Persisted file is metadata-only (name+size); the original File blob
        // can't be recovered, but we don't need it once the upload is done.
        file: lr.fileName ? { name: lr.fileName, size: lr.fileSize || 0 } : null,
        total: lr.total || 0,
        processed: lr.processed || 0,
        live: lr.live || 0,
        staged: lr.staged || 0,
        rejected: lr.rejected || 0,
        rowFailures: lr.rowFailures || [],
        parsedRows: lr.parsedRows || null,
        summary: lr.summary || null,
        incompleteReason: lr.incompleteReason || null,
      };
    }

    default:
      return state;
  }
}

// Synchronous client-side required-field check. Mirrors the server's Tier 1
// so the user can fix obvious mistakes (blank category, missing barcode) without
// paying for a round-trip. Synthesizes ERR_REQUIRED_FIELD_MISSING entries with
// the same shape as server-emitted errors so the rendering path is unified.
function validateRowsClientSide(rows) {
  const failures = [];
  rows.forEach((row, idx) => {
    const missing = REQUIRED_FIELDS.filter((f) => {
      const v = row[f];
      return v == null || String(v).trim() === "";
    });
    if (missing.length > 0) {
      failures.push({
        rowIndex: idx,
        name: row.name ? String(row.name) : null,
        barcode: row.barcode ? String(row.barcode) : null,
        errors: missing.map((f) => ({
          code: "ERR_REQUIRED_FIELD_MISSING",
          params: { field: f },
          field: f,
        })),
      });
    }
  });
  return failures;
}

// Phases worth persisting — the user may want to come back to this list later.
// Successful uploads have nothing to come back to, so we don't save them.
const PERSISTED_PHASES = [
  PHASES.DONE_WITH_ERRORS,
  PHASES.CLIENT_ERROR,
  PHASES.CANCELLED,
  PHASES.INCOMPLETE,
];

export function useBulkStageUpload() {
  const [state, dispatch] = useReducer(reducer, initialState);
  // `lastResult` is the persisted record of the previous upload (loaded once
  // on mount). It's separate from in-memory `state` so the upload screen can
  // start in IDLE and offer "resume from last upload" rather than auto-restoring.
  const [lastResult, setLastResult] = useState(() => loadLastResult());
  const abortControllerRef = useRef(null);

  // Save-on-terminal-phase. We key the effect on state.phase so it fires once
  // per transition; reading state.* inside is safe because the closure captures
  // the render that scheduled the effect, which has the fully-applied state.
  useEffect(() => {
    if (!PERSISTED_PHASES.includes(state.phase)) return;
    const saved = saveLastResult({
      phase: state.phase,
      fileName: state.file?.name,
      fileSize: state.file?.size,
      total: state.total,
      processed: state.processed,
      live: state.live,
      staged: state.staged,
      rejected: state.rejected,
      rowFailures: state.rowFailures,
      parsedRows: state.parsedRows,
      summary: state.summary,
      incompleteReason: state.incompleteReason,
    });
    if (saved) setLastResult(loadLastResult());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: persist on phase transition only
  }, [state.phase]);

  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    dispatch({ type: "RESET" });
  }, []);

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // The stream loop will throw AbortError; the catch in startUpload
    // dispatches STREAM_ABORTED. Don't pre-empt here — wait for the catch.
  }, []);

  const startUpload = useCallback(async (file) => {
    dispatch({ type: "PARSING_STARTED", file });

    let parsed;
    try {
      parsed = await parseProductsFile(file);
    } catch (err) {
      dispatch({
        type: "PARSE_FAILED",
        message: err?.message || "Couldn't read the file.",
      });
      return;
    }

    const rows = parsed.rows || [];

    if (rows.length === 0) {
      dispatch({
        type: "CLIENT_VALIDATION_FAILED",
        parsedRows: rows,
        failures: [{
          rowIndex: 0,
          name: null,
          barcode: null,
          errors: [{ code: "ERR_EMPTY_PAYLOAD", params: {} }],
        }],
      });
      return;
    }

    if (rows.length > MAX_ROWS) {
      dispatch({
        type: "CLIENT_VALIDATION_FAILED",
        parsedRows: rows,
        failures: [{
          rowIndex: 0,
          name: null,
          barcode: null,
          errors: [{
            code: "ERR_TOO_MANY_ROWS",
            params: { max: MAX_ROWS, got: rows.length },
          }],
        }],
      });
      return;
    }

    const clientFailures = validateRowsClientSide(rows);
    if (clientFailures.length > 0) {
      dispatch({
        type: "CLIENT_VALIDATION_FAILED",
        parsedRows: rows,
        failures: clientFailures,
      });
      return;
    }

    // All clear — start the stream.
    dispatch({ type: "UPLOAD_STARTED", parsedRows: rows });

    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Track summary + row counts in the closure so we can decide between
    // INCOMPLETE (got some rows then died) vs TRANSPORT_ERROR (never got any
    // rows) without reading stale React state.
    let sawSummary = false;
    let lastProcessed = 0;

    try {
      await streamBulkStage({
        products: rows,
        signal: controller.signal,
        onEvent: (event) => {
          if (event?.event === "summary") sawSummary = true;
          if (event?.event === "row") lastProcessed += 1;
          dispatch({ type: "STREAM_EVENT", event });
        },
      });
      // Stream closed cleanly without a summary — partial result. Branch on
      // whether we got any rows at all so the user sees the right card.
      if (!sawSummary && !controller.signal.aborted) {
        if (lastProcessed > 0) {
          dispatch({ type: "STREAM_INCOMPLETE", reason: "stream-ended-without-summary" });
        } else {
          dispatch({
            type: "TRANSPORT_ERROR",
            message:
              "The server stopped responding before finishing the upload. " +
              "If this keeps happening, ask the backend team to check the server logs.",
          });
        }
      }
    } catch (err) {
      if (err?.name === "AbortError" && controller.signal.aborted) {
        // User-initiated cancel.
        dispatch({ type: "STREAM_ABORTED" });
      } else if (err instanceof BulkStageStreamTimeoutError) {
        if (lastProcessed > 0) {
          dispatch({ type: "STREAM_INCOMPLETE", reason: "inactivity-timeout" });
        } else {
          dispatch({
            type: "TRANSPORT_ERROR",
            message:
              `${err.message} The server didn't return any rows before timing out — ` +
              "ask the backend team to check the server logs.",
          });
        }
      } else if (err instanceof BulkStageRequestError) {
        dispatch({
          type: "REQUEST_ERROR",
          error: { code: err.code, message: err.message, params: err.params },
        });
      } else {
        dispatch({
          type: "TRANSPORT_ERROR",
          message: err?.message || "Upload failed. Please retry.",
        });
      }
    } finally {
      abortControllerRef.current = null;
    }
  }, []);

  // Restore the persisted last result into in-memory state so the failure
  // cards re-render. Used by the resume banner on the IDLE screen.
  const restoreLastResult = useCallback(() => {
    if (!lastResult) return;
    dispatch({ type: "HYDRATE_FROM_PERSIST", lastResult });
  }, [lastResult]);

  // Forget the persisted result. Used by the "Dismiss" affordance on the
  // resume banner — the user has decided they don't need it anymore.
  const dismissLastResult = useCallback(() => {
    clearLastResult();
    setLastResult(null);
  }, []);

  return {
    state,
    lastResult,
    actions: {
      startUpload,
      cancel,
      reset,
      restoreLastResult,
      dismissLastResult,
    },
  };
}
